// src/pages/SingleListingGenerator/components/KeyFeaturesForm.js
import React, { useState } from 'react';
import styles from './KeyFeaturesForm.module.css';

const KeyFeaturesForm = ({ formData, handleChange, isLoading, listingMode, isExpanded, showKeyFeaturesError, hideKeyFeatures = false, onEnableVariantsTab, invalidFields = new Set(), briefDescriptionRef, keyFeaturesRef, howIsUsedRef }) => {
  const FEATURE_FIELDS = ['feature1', 'feature2', 'feature3', 'feature4', 'feature5'];

  const getFeatures = () => {
    return FEATURE_FIELDS.map(field => formData[field]).filter(Boolean);
  };

  const [featureInput, setFeatureInput] = useState('');
  const features = getFeatures();

  const commitFeatureInput = () => {
    if (!featureInput.trim()) return;
    const newFeature = featureInput.trim();
    if (features.includes(newFeature)) {
      setFeatureInput('');
      return;
    }
    const emptyField = FEATURE_FIELDS.find(field => !formData[field]);
    if (emptyField) {
      handleChange({ target: { name: emptyField, value: newFeature } });
    }
    setFeatureInput('');
  };

  const addFeature = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitFeatureInput();
    }
  };

  const removeFeature = (featureToRemove) => {
    const fieldWithFeature = FEATURE_FIELDS.find(field => formData[field] === featureToRemove);
    if (fieldWithFeature) {
      handleChange({ target: { name: fieldWithFeature, value: '' } });
    }
  };

  // Product Benefits, Where is it Used, How is it Used - leave empty for manual entry
  // All features (feature1-feature5) now go in Key Features section only
  // These column sections use separate fields and remain empty (just placeholders) when auto-filled
  const benefits = formData.benefits || '';
  const usage = formData.usage || '';
  const instructions = formData.instructions || '';

  return (
    <div className={styles.descriptionContainer}>
      {/* Product Description Section */}
      <div ref={briefDescriptionRef} className={styles.section}>
        <h3 className={styles.sectionTitle}>
          Brief Description<span className={styles.requiredAsterisk}>*</span>
          {invalidFields.has('briefDescription') && <span className={styles.requiredLabel}>required*</span>}
        </h3>
        <div className={styles.inputWrapper}>
          <textarea
            name="briefDescription"
            className={styles.textarea}
            placeholder="Mention what is the product, how it works & why a customer should buy it"
            value={formData.briefDescription || ''}
            onChange={handleChange}
            disabled={isLoading}
            rows="4"
            maxLength="700"
          />
        </div>
      </div>

      {/* Key Features Section */}
      {(!hideKeyFeatures || listingMode === 'new') && (
        <div ref={keyFeaturesRef} className={styles.section}>
          <h3 className={styles.sectionTitle}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g opacity="0.7">
                <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="#6794C5"/>
                <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="#6794C5"/>
              </g>
            </svg>
            Key Features<span className={styles.requiredAsterisk}>*</span>
          </h3>
          <div className={styles.inputWrapper}>
            <div className={styles.featuresContainer}>
              {features.map((feature, index) => (
                <div key={index} className={styles.featureChip}>
                  <span>{feature}</span>
                  <button
                    type="button"
                    className={styles.removeChip}
                    onClick={() => removeFeature(feature)}
                  >
                    ×
                  </button>
                </div>
              ))}
              <input
                type="text"
                name="feature1"
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onKeyDown={addFeature}
                onBlur={commitFeatureInput}
                disabled={isLoading}
                placeholder={features.length === 0 ? "Type and press Enter to add features" : ""}
                className={styles.featureInput}
              />
            </div>
          </div>
          {showKeyFeaturesError && !formData.feature1?.trim() && (
            <div className={styles.errorText}>please enter atleast one feature</div>
          )}
        </div>
      )}

      {/* Three Column Section */}
      {(!hideKeyFeatures || listingMode === 'new') && (
        <div className={styles.threeColumnGrid}>
          <div className={styles.columnSection}>
            <h3 className={styles.columnTitle}>Product Benefits</h3>
            <input
              type="text"
              name="benefits"
              className={styles.columnInput}
              placeholder="Cotton 100%"
              value={benefits}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <div className={styles.columnSection}>
            <h3 className={styles.columnTitle}>Where is it Used?</h3>
            <input
              type="text"
              name="usage"
              className={styles.columnInput}
              placeholder="Anywhere"
              value={usage}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <div ref={howIsUsedRef} className={styles.columnSection}>
            <h3 className={styles.columnTitle}>How is it Used?</h3>
            <input
              type="text"
              name="instructions"
              className={styles.columnInput}
              placeholder="Enter here"
              value={instructions}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>
        </div>
      )}

      {/* Application Fields Section */}
      {(!hideKeyFeatures || listingMode === 'new') && (
        <div className={styles.threeColumnGrid}>
          <div className={styles.columnSection}>
            <h3 className={styles.columnTitle}>Application 1</h3>
            <input
              type="text"
              name="application1"
              className={styles.columnInput}
              placeholder="Enter application"
              value={formData.application1 || ''}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <div className={styles.columnSection}>
            <h3 className={styles.columnTitle}>Application 2</h3>
            <input
              type="text"
              name="application2"
              className={styles.columnInput}
              placeholder="Enter application"
              value={formData.application2 || ''}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <div className={styles.columnSection}>
            <h3 className={styles.columnTitle}>Application 3</h3>
            <input
              type="text"
              name="application3"
              className={styles.columnInput}
              placeholder="Enter application"
              value={formData.application3 || ''}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>
        </div>
      )}

      {/* Add Variations Button Section */}
      <div className={styles.addVariationsSection}>
        <span className={styles.addVariationsText}>Add Variations</span>
        <button
          type="button"
          className={styles.addVariationsButton}
          disabled={isLoading}
          onClick={() => {
            if (onEnableVariantsTab) {
              onEnableVariantsTab();
            }
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 3V13M3 8H13" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

    </div>
  );
};

export default KeyFeaturesForm;